import { z } from 'zod';

import { UserRoleSchema } from './auth.js';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * My Profile (ADR-0040). Self-service endpoints under `/me`:
 *   GET    /me                      - profile + activity summary
 *   PATCH  /me                      - editable subset
 *   POST   /me/change-password      - current + new password
 *
 * Editable subset is intentionally tiny in Phase 1 (fullName, phone).
 * Avatar upload, salutation, and 2FA come in a follow-up that adds the
 * underlying User columns first.
 */

export const MeProfileSchema = z.object({
  user: z.object({
    id: UuidSchema,
    email: z.string(),
    fullName: z.string(),
    phone: z.string().nullable(),
    /// Agent's CallerDesk member number - the chat widget prefills it into
    /// WhatsApp template params ("reach your travel expert at ...").
    callerDeskNumber: z.string().nullable(),
    employeeCode: z.string().nullable(),
    // Narrowed from `z.string()` (2026-08-04) so callers can trust this as the
    // live role - `useSyncSessionUser` writes it back into the auth store.
    role: UserRoleSchema,
    branch: z
      .object({
        id: UuidSchema,
        name: z.string(),
      })
      .nullable(),
    department: z
      .object({
        id: UuidSchema,
        name: z.string(),
      })
      .nullable(),
    lastLogin: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
  }),
  activitySummary: z.object({
    leadsOwned: z.number().int(),
    followUpsDueToday: z.number().int(),
    followUpsCompletedThisWeek: z.number().int(),
  }),
  /// Effective permission codes (own + every parent role in the chain).
  /// Drives the admin UI's nav-item visibility + per-route guards
  /// (ADR-0039 Phase 3). Empty array if the user has no role-table row
  /// yet, in which case clients fall back to the legacy @Roles enum
  /// check on the user object.
  effectivePermissions: z.array(z.string()),
});
export type MeProfile = z.infer<typeof MeProfileSchema>;

export const UpdateMeRequestSchema = z.object({
  fullName: z.string().trim().min(1).max(120).optional(),
  phone: z.string().trim().max(30).nullable().optional(),
});
export type UpdateMeRequest = z.infer<typeof UpdateMeRequestSchema>;

export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z
    .string()
    .min(8, 'New password must be at least 8 characters')
    .max(128, 'New password is too long'),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;
